// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// Licensed under the Amazon Software License
// http://aws.amazon.com/asl/

import { types } from 'mobx-state-tree';
import { toErr, Err } from '../Err';

// A four-state model that has the following states:
//  +---------+                   +-----------+
//  | initial |            +----> |   ready   |
//  +---------+            |      +-----------+
//                         |
//      + ^                |         + ^
//      | |                |         | | success
// load | | error          |    load | | or error
//      v +                |         v +
//                         |
//  +---------+            |      +-----------+
//  | loading +------------+      | reloading |
//  +---------+   success         +-----------+
//
// state: <initial|loading|ready|reloading>
// error: <error object> if there is an error otherwise <undefined>
// empty: <true> if state is ready or reloading and the content is considered empty
// This can be used as a base for most of the Mobx State Tree stores and it provides
// useful methods for polling backend apis etc
const BaseStore = types.model('BaseStore', {
    state: 'initial',
    error: types.maybe(Err),
    tickPeriod: 7 * 1000, // 7 seconds
    pollingInterval: 0,
  })
  .actions(() => ({
    // Had issues using runInAction from mobx
    // the issue is discussed here https://github.com/mobxjs/mobx-state-tree/issues/915
    runInAction(fn) {
      return fn();
    }
  }))
  .actions(self => {
    let loadingPromise;

    return {
      load: (...args) => {
        if (loadingPromise) return loadingPromise;

        self.error = undefined;
        if (self.state === 'ready') self.state = 'reloading';
        else self.state = 'loading';

        loadingPromise = new Promise((resolve, reject) => {
          // if ((self.state === 'loading') || (self.state === 'reloading')) return;
          try {
            // the stores that extend this base store need to implement doLoad method that loads store
            self.doLoad(...args)
              .then(() => {
                self.runInAction(() => { self.state = 'ready'; });
                loadingPromise = undefined;
                resolve();
              })
              .catch((err) => {
                self.runInAction(() => {
                  self.state = (self.state === 'loading')? 'initial': 'ready';
                  self.error = toErr(err);
                });
                loadingPromise = undefined;
                reject(err);
              });
          } catch(err) {
            self.runInAction(() => {
              self.state = (self.state === 'loading')? 'initial': 'ready';
              self.error = toErr(err);
            });
            loadingPromise = undefined;
            reject(err);
          }
        });

        return loadingPromise;
      },

      startPolling: () => {
        if (self.pollingInterval !== 0) return; // there is one running
        if (!self.shouldPoll()) return;
        const id = setInterval(async () => {
          if (!self.shouldPoll()) return;
          try {
            await self.load();
          } catch (err) { /* ignore */ };
        }, self.tickPeriod);
        self.pollingInterval = id;
      },

      shouldPoll: () => {
        return true; // extender can override this method
      },

      stopPolling: () => {
        const id = self.pollingInterval;
        if (id !== 0) {
          clearInterval(id);
          self.pollingInterval = undefined;
        }
      },
      changeTickPeriod: function(period) {
        const beating = self.heartBeating;
        self.tickPeriod = period;
        if (beating) {
          self.stopPolling();
          self.startPolling();
        }
      },
      cleanup: () => {
        self.stopPolling();
        self.state = 'initial';
        self.error = undefined;
      }
    };
  })

  .views(self => ({
    get heartBeating() {
      return self.pollingInterval > 0;
    },
    get initial() {
      return self.state === 'initial';
    },
    get ready() {
      return self.state === 'ready';
    },
    get loading() {
      return self.state === 'loading';
    },
    get reloading() {
      return self.state === 'reloading';
    },
    get errorMessage() {
      return self.error? self.error.message || 'unknown error': '';
    }
  }));

const isStoreReady = (obj) => (obj.ready || obj.reloading);
const isStoreEmpty = (obj) => ((obj.ready || obj.reloading) && obj.empty);
const isStoreNotEmpty = (obj) => ((obj.ready || obj.reloading) && !obj.empty);
const isStoreLoading = (obj) => obj.loading;
const isStoreReloading = (obj) => obj.reloading;
const isStoreNew = (obj) => obj.initial;
const isStoreError = (obj) => !!obj.error;

export {
  BaseStore,
  isStoreReady,
  isStoreEmpty,
  isStoreNotEmpty,
  isStoreLoading,
  isStoreReloading,
  isStoreNew,
  isStoreError,
};
